import SimpleNotification from "components/Notification/SimpleNotification";
import { toast } from "react-toastify";
import { isUiWebsocketDown } from "utils/NotificationManager/connectionState";

// Number of toasts currently displayed. react-toastify's onChange callback is
// the single source of truth for this count. NOTE: this total INCLUDES the
// "Close all" helper toast itself whenever it is shown.
let activeToastCount = 0;
// Id of the "Close all" helper toast while it is displayed, else null.
let dismissAllToastId = null;
// Re-entrancy guard: creating the "Close all" toast can itself trigger
// onChange; without this we could spawn it more than once.
let creatingDismissAllToast = false;

const commonOptions = {
    draggable: false,
};

// see https://fkhadra.github.io/react-toastify/introduction
export default class NotificationService {
    static info(content, options) {
        return toast.info(content, { ...commonOptions, ...options });
    }

    static success(content, options) {
        return toast.success(content, { ...commonOptions, ...options });
    }

    static warn(content, options) {
        return toast.warn(content, {
            autoClose: 15000,
            ...commonOptions,
            ...options,
        });
    }

    static error(content, options) {
        return toast.error(content, {
            autoClose: 5000,
            ...commonOptions,
            ...options,
        });
    }

    // Connection-error toast for a *secondary* live-data channel (per-record
    // updates, resource-stats widgets, dependency visualization). While the
    // primary `/ui` websocket is down, the top "Connection to backend lost"
    // banner is already the single persistent signal for the outage, so these
    // secondary sockets' reconnect-storm error events must NOT each raise a
    // fresh toast on top of it (the reported repeating-toast defect). When the
    // primary socket is up, an isolated secondary failure is a genuine problem
    // and the toast still fires. Returns null when suppressed.
    static liveDataError(content, options) {
        if (isUiWebsocketDown()) {
            return null;
        }
        return NotificationService.error(content, options);
    }

    static dismiss(id) {
        toast.dismiss(id);
    }

    static dismissAll() {
        toast.dismiss();
        dismissAllToastId = null;
    }

    // Show the "Close all" helper toast only when at least two *content* toasts
    // are visible. The "Close all" toast must never count itself toward that
    // threshold, otherwise it lingers (or appears) while only a single real
    // toast remains.
    static checkDismissAllToast() {
        const contentToastCount = activeToastCount - (dismissAllToastId ? 1 : 0);

        if (
            contentToastCount > 1 &&
            !dismissAllToastId &&
            !creatingDismissAllToast
        ) {
            creatingDismissAllToast = true;
            dismissAllToastId = toast.info(
                <SimpleNotification title="Close all toast messages" />,
                {
                    ...commonOptions,
                    autoClose: false,
                    closeButton: (
                        <button
                            className="button button--white button--small"
                            onClick={() => {
                                NotificationService.dismissAll();
                            }}
                        >
                            Close all
                        </button>
                    ),
                    className: "close-all-toasts",
                    onClose: () => {
                        dismissAllToastId = null;
                    },
                }
            );
            creatingDismissAllToast = false;
        } else if (contentToastCount <= 1 && dismissAllToastId) {
            NotificationService.dismiss(dismissAllToastId);
            dismissAllToastId = null;
        }
    }
}

toast.onChange((count) => {
    activeToastCount = count;
    NotificationService.checkDismissAllToast();
});
